'use client'

// Product/test switcher in the top nav — the single source of "which test am I
// in". One emerald UnlimitedTests brand across every test; the ACTIVE test is
// signalled by this labeled pill (monogram + name), not by recoloring the
// chrome. This scales to any number of tests where a per-test palette would not.
//
// Visibility is decided server-side (see site-nav.tsx): the switcher only
// renders when the user actually has a second test to switch to, so it's a
// no-op for the SAT-only majority.
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { ChevronDown, Check } from 'lucide-react'

export type TestKey = 'SAT' | 'ACT' | 'PSAT' | 'AP'

type TestOption = {
  key: TestKey
  label: string
  href: string
  monogram: string
  preview?: boolean
  soon?: boolean
}

const TESTS: TestOption[] = [
  { key: 'SAT', label: 'SAT', href: '/dashboard', monogram: 'SAT' },
  { key: 'ACT', label: 'ACT', href: '/act/dashboard', monogram: 'ACT', preview: true },
  { key: 'PSAT', label: 'PSAT', href: '/psat', monogram: 'PSA', soon: true },
  { key: 'AP', label: 'AP Exams', href: '/ap', monogram: 'AP', soon: true },
]

export default function TestSwitcher() {
  const pathname = usePathname()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  // Close on route change.
  useEffect(() => setOpen(false), [pathname])

  const activeKey: TestKey = pathname?.startsWith('/act') ? 'ACT' : 'SAT'
  const active = TESTS.find((t) => t.key === activeKey) ?? TESTS[0]

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Current test: ${active.label}. Switch test.`}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-full border border-white/35 bg-white/15 py-1 pl-1 pr-2.5 text-sm font-semibold text-white transition hover:bg-white/25"
      >
        {/* Inline color: the .nav-gradient header forces white text on every
            descendant, which would make this dark-on-white tile invisible. */}
        <span
          className="flex h-6 w-8 items-center justify-center rounded-md bg-white text-[10px] font-bold tracking-wide"
          style={{ color: '#064e3b' }}
        >
          {active.monogram}
        </span>
        {active.label}
        <ChevronDown className="h-3.5 w-3.5 opacity-80" aria-hidden />
      </button>

      {open && (
        <div
          role="menu"
          // data-nav-popover opts this dropdown OUT of the .nav-gradient header's
          // "force white text on everything" cascade — without it the menu labels
          // render white-on-white and vanish (see globals.css).
          data-nav-popover
          className="absolute left-0 top-full z-50 mt-1.5 w-60 rounded-xl border border-border bg-popover p-1.5 text-foreground shadow-lg"
        >
          <p className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Switch test
          </p>
          {TESTS.map((t) => {
            const isActive = t.key === activeKey
            const content = (
              <>
                <span
                  className={`flex h-7 w-9 items-center justify-center rounded-md text-[10px] font-bold ${
                    t.soon ? 'bg-muted' : 'bg-emerald-700 dark:bg-emerald-600'
                  }`}
                  // Inline color beats the data-nav-popover dark-text rule so the
                  // white monogram stays legible on the dark tile.
                  style={{ color: t.soon ? undefined : '#fff' }}
                >
                  {t.monogram}
                </span>
                <span className="flex-1 text-left">{t.label}</span>
                {t.preview && !t.soon && (
                  <span className="rounded-full bg-brand-tint px-1.5 py-0.5 text-[10px] font-medium text-brand-ink">
                    Preview
                  </span>
                )}
                {t.soon && <span className="text-[11px] text-muted-foreground">Soon</span>}
                {isActive && <Check className="h-4 w-4 text-brand-ink" aria-hidden />}
              </>
            )
            const cls = `flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm ${
              isActive ? 'bg-brand-tint/60' : 'hover:bg-muted'
            }`
            return t.soon ? (
              <div key={t.key} className={`${cls} cursor-default opacity-70`} aria-disabled>
                {content}
              </div>
            ) : (
              <Link key={t.key} href={t.href} role="menuitem" className={cls}>
                {content}
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}
